import type { ProjectPreviewArgs } from '@dreamstack/feature-components/src/projects/ProjectPreview'
import { ProjectPreview } from '@dreamstack/feature-components/src/projects/ProjectPreview'
import { ProjectPreviewLayout } from '@dreamstack/feature-components/src/layout/ProjectPreviewLayout'
import { transformPropstackImageUrl } from '@dreamstack/feature-components/src/property/propstack-image-url-transform'
import { BodyText, HeadingH1 } from '@dreamstack/feature-components/src/layout/Texts'
import type { ComingSoonPreviewArgs } from '@dreamstack/feature-components/src/projects/ComingSoonPreview'
import { ComingSoonPreview } from '@dreamstack/feature-components/src/projects/ComingSoonPreview'
import { useTranslation } from '@dreamstack/i18n'
import map from 'lodash/map'
import type { FunctionComponent } from 'react'
import { PageBlockSpacingContainer } from '../../contentful/PageBlockSpacingContainer'

export type ProjectOverviewListProps = {
  projects?: ProjectPreviewArgs[]
  comingSoonProjects?: ComingSoonPreviewArgs[]
  onlyShowComingSoon?: boolean
  onlyShowCurrentProjects?: boolean
}

export const ProjectOverviewList: FunctionComponent<
  React.PropsWithChildren<ProjectOverviewListProps>
> = ({
  projects,
  comingSoonProjects,
  onlyShowComingSoon,
  onlyShowCurrentProjects,
}) => {
  const t = useTranslation()

  const projectsShown = projects

  const showOnlyProjectsButNoProjectExists =
    onlyShowCurrentProjects && (!projectsShown || !projectsShown.length)
  const showOnlyComingSoonButNoComingSoonExists =
    onlyShowComingSoon && (!comingSoonProjects || !comingSoonProjects.length)
  const nothingToDisplay =
    (!comingSoonProjects || !comingSoonProjects.length) &&
    (!projectsShown || !projectsShown.length)

  if (
    showOnlyProjectsButNoProjectExists ||
    showOnlyComingSoonButNoComingSoonExists ||
    nothingToDisplay
  ) {
    return (
      <>
        <BodyText>{t('accentro:projectPreview.noResults')}</BodyText>
      </>
    )
  }

  // only map the projects if either "onlyShowCurrentProjects" is set to true or if both "onlyShowComingSoon" and "onlyShowCurrentProjects" are set to undefined (used as customblock and both should be rendered)
  const projectsMap =
    onlyShowCurrentProjects || (!onlyShowComingSoon && !onlyShowCurrentProjects)
      ? map(projectsShown, (project) => ({
          ...project,

          // Transform titleImageUrl for NextImage encoding bug
          titleImageUrl: transformPropstackImageUrl({
            url: project.titleImageUrl,
          }),
        }))
      : []

  // only map the projects if either "onlyShowComingSoon" is set to true or if both "onlyShowComingSoon" and "onlyShowCurrentProjects" are set to undefined (component used as customblock and both should be rendered)
  const comingSoonMap =
    onlyShowComingSoon || (!onlyShowComingSoon && !onlyShowCurrentProjects)
      ? comingSoonProjects?.map((comingSoonProject) => ({
          ...comingSoonProject,
          id: comingSoonProject.id,
          titleImageUrl: transformPropstackImageUrl({
            url: comingSoonProject.titleImageUrl,
          }),
        }))
      : []

  return (
    <PageBlockSpacingContainer>
      {/* if only current projects should be shown, dont render coming soon objects */}
      {!onlyShowCurrentProjects && (
        <>
          <div>
            {onlyShowComingSoon ? (
              <HeadingH1>
                {' '}
                {t('accentro:comingSoon.ComingSoonPageHeadline')}
              </HeadingH1>
            ) : (
              <HeadingH1>Coming Soon</HeadingH1>
            )}

            <BodyText className="my-px-24">
              {t('accentro:comingSoon.subtitle')}
            </BodyText>
          </div>
          <ProjectPreviewLayout>
            {(comingSoonMap ?? []).map((comingSoon) => (
              <ComingSoonPreview key={comingSoon.id} project={comingSoon} />
            ))}
          </ProjectPreviewLayout>
        </>
      )}
      {/* if only coming soon projects should be shown, dont render current objects */}
      {!onlyShowComingSoon && (
        <>
          {onlyShowCurrentProjects ? null : (
            <HeadingH1>{t('accentro:projects.furtherRealEstate')}</HeadingH1>
          )}
          <ProjectPreviewLayout>
            {projectsMap.map((project) => (
              <ProjectPreview key={project.id} project={project} />
            ))}
          </ProjectPreviewLayout>
        </>
      )}
    </PageBlockSpacingContainer>
  )
}
